import { motion } from "framer-motion";

/**
 * CartoonStickers — hand-built cartoon SVG illustrations specifically for
 * AllTask Moves. Brand palette:
 *   - Navy   #0E2A47
 *   - Orange #F26B1F
 *   - Cream  #FAF6EE
 *
 * Every sticker is wrapped in <CartoonSticker> which adds:
 *   - Scroll-reveal entrance
 *   - Continuous float animation
 *   - Optional tilt + soft drop shadow
 */

const NAVY = "#0E2A47";
const NAVY_SOFT = "#1A3D5E";
const ORANGE = "#F26B1F";
const ORANGE_SOFT = "#FFB585";
const CREAM = "#FFE7C9";
const PAPER = "#FFF5E5";
const SHADOW = "rgba(14, 42, 71, 0.14)";

/* ---------- 1. Moving truck ---------- */
export const TruckSticker = (props) => (
    <svg viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <defs>
            <linearGradient id="truck-body" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={ORANGE} />
                <stop offset="100%" stopColor="#D9591A" />
            </linearGradient>
        </defs>
        <ellipse cx="60" cy="80" rx="50" ry="4" fill={SHADOW} />
        <rect x="6" y="22" width="60" height="46" rx="6" fill="url(#truck-body)" />
        <rect x="14" y="30" width="44" height="20" rx="3" fill={PAPER} stroke={NAVY} strokeWidth="2" />
        <text x="36" y="44" textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="8" fontWeight="800" fill={NAVY}>ATM</text>
        <path d="M66 32 L82 32 L94 50 L94 68 L66 68 Z" fill={NAVY} />
        <rect x="72" y="38" width="14" height="10" rx="2" fill={CREAM} />
        <circle cx="30" cy="72" r="9" fill={NAVY} />
        <circle cx="30" cy="72" r="3.5" fill={ORANGE} />
        <circle cx="82" cy="72" r="9" fill={NAVY} />
        <circle cx="82" cy="72" r="3.5" fill={ORANGE} />
        {/* puff */}
        <circle cx="100" cy="46" r="4" fill={ORANGE_SOFT} opacity=".55" />
        <circle cx="108" cy="42" r="3" fill={ORANGE_SOFT} opacity=".4" />
    </svg>
);

/* ---------- 2. Box stack (moving boxes, not a cake!) ---------- */
export const BoxStackSticker = (props) => (
    <svg viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="60" cy="92" rx="48" ry="3.5" fill={SHADOW} />
        {/* Back / smaller box — tilted left */}
        <g transform="rotate(-7 32 60)">
            <rect x="6" y="38" width="48" height="40" rx="3" fill="#E8C18C" stroke={NAVY} strokeWidth="2" />
            {/* packing tape — horizontal */}
            <rect x="6" y="48" width="48" height="5" fill="#F5DEAA" stroke={NAVY} strokeWidth="1.2" />
            {/* arrow-up icons */}
            <path d="M16 64 l3 -4 l3 4 M19 60 V70" stroke={NAVY} strokeWidth="1.6" strokeLinecap="round" fill="none" />
            <path d="M30 64 l3 -4 l3 4 M33 60 V70" stroke={NAVY} strokeWidth="1.6" strokeLinecap="round" fill="none" />
        </g>
        {/* Front / bigger box — tilted right */}
        <g transform="rotate(6 78 70)">
            <rect x="48" y="32" width="60" height="52" rx="3" fill="#D9A668" stroke={NAVY} strokeWidth="2" />
            {/* packing tape — horizontal across the seam */}
            <rect x="48" y="46" width="60" height="6" fill="#F5DEAA" stroke={NAVY} strokeWidth="1.2" />
            {/* packing tape — vertical seam */}
            <rect x="74" y="32" width="6" height="14" fill="#F5DEAA" stroke={NAVY} strokeWidth="1.2" />
            {/* fragile label */}
            <rect x="55" y="58" width="32" height="14" rx="2" fill="#FFF6E2" stroke={NAVY} strokeWidth="1.4" />
            <text
                x="71"
                y="68"
                textAnchor="middle"
                fontFamily="Manrope, sans-serif"
                fontSize="6"
                fontWeight="800"
                fill={ORANGE}
                letterSpacing="0.5"
            >
                FRAGILE
            </text>
            {/* small "this side up" arrow */}
            <path d="M93 64 l3 -4 l3 4 M96 60 V70" stroke={NAVY} strokeWidth="1.6" strokeLinecap="round" fill="none" />
        </g>
    </svg>
);

/* ---------- 3. Mover person ---------- */
export const MoverPersonSticker = (props) => (
    <svg viewBox="0 0 90 120" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="45" cy="112" rx="30" ry="3.5" fill={SHADOW} />
        {/* legs */}
        <rect x="32" y="78" width="9" height="28" rx="3" fill={NAVY} />
        <rect x="49" y="78" width="9" height="28" rx="3" fill={NAVY} />
        <rect x="30" y="103" width="13" height="6" rx="2" fill={NAVY_SOFT} />
        <rect x="47" y="103" width="13" height="6" rx="2" fill={NAVY_SOFT} />
        {/* body */}
        <path d="M22 50 Q22 38 35 38 H55 Q68 38 68 50 V82 H22 Z" fill={ORANGE} />
        <rect x="28" y="56" width="34" height="4" rx="2" fill="#FFF" opacity=".6" />
        {/* head */}
        <circle cx="45" cy="28" r="11" fill={CREAM} stroke={NAVY} strokeWidth="2" />
        <circle cx="41" cy="28" r="1.4" fill={NAVY} />
        <circle cx="49" cy="28" r="1.4" fill={NAVY} />
        <path d="M41 32 q4 3 8 0" stroke={NAVY} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        {/* hair */}
        <path d="M34 24 q11 -16 22 0 q-11 -6 -22 0 Z" fill={NAVY} />
        {/* arms holding box */}
        <path d="M22 50 q-6 4 -2 14 l8 4 l4 -10 z" fill={ORANGE} />
        <path d="M68 50 q6 4 2 14 l-8 4 l-4 -10 z" fill={ORANGE} />
        {/* box */}
        <rect x="22" y="58" width="46" height="22" rx="3" fill={PAPER} stroke={NAVY} strokeWidth="2" />
        <path d="M22 64 H68" stroke={NAVY} strokeDasharray="3 3" strokeWidth="1.6" />
        <rect x="40" y="58" width="10" height="4" fill={ORANGE} />
    </svg>
);

/* ---------- 4. House with map pin ---------- */
export const HousePinSticker = (props) => (
    <svg viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="55" cy="100" rx="40" ry="4" fill={SHADOW} />
        <path d="M20 56 L55 26 L90 56 V94 H20 Z" fill={CREAM} stroke={NAVY} strokeWidth="2" />
        <path d="M14 58 L55 22 L96 58" stroke={ORANGE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="44" y="64" width="22" height="30" rx="2" fill={NAVY} />
        <circle cx="61" cy="80" r="1.6" fill={ORANGE} />
        <rect x="28" y="64" width="12" height="12" rx="1.5" fill={PAPER} stroke={NAVY} strokeWidth="1.6" />
        <rect x="70" y="64" width="12" height="12" rx="1.5" fill={PAPER} stroke={NAVY} strokeWidth="1.6" />
        {/* map pin */}
        <g transform="translate(78 16)">
            <path d="M0 14 C0 5 14 5 14 14 C14 22 7 30 7 30 C7 30 0 22 0 14 Z" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
            <circle cx="7" cy="14" r="3.5" fill="#FFF" />
        </g>
    </svg>
);

/* ---------- 5. Keys ---------- */
export const KeyTagSticker = (props) => (
    <svg viewBox="0 0 110 90" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="55" cy="80" rx="35" ry="3.5" fill={SHADOW} />
        <circle cx="28" cy="40" r="18" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
        <circle cx="28" cy="40" r="7" fill="#FFF" />
        <rect x="42" y="36" width="50" height="8" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
        <rect x="78" y="44" width="6" height="10" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
        <rect x="68" y="44" width="6" height="6" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
        {/* tag */}
        <g transform="rotate(-12 18 18)">
            <rect x="2" y="2" width="22" height="14" rx="3" fill={CREAM} stroke={NAVY} strokeWidth="1.6" />
            <circle cx="6" cy="9" r="1.2" fill={NAVY} />
            <path d="M11 9 H21" stroke={NAVY} strokeWidth="1.4" strokeLinecap="round" />
        </g>
    </svg>
);

/* ---------- 6. Plant ---------- */
export const PlantSticker = (props) => (
    <svg viewBox="0 0 80 100" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="40" cy="92" rx="25" ry="3" fill={SHADOW} />
        <path d="M40 64 C30 54 18 50 18 36 C28 36 38 44 40 56 Z" fill="#0E2A47" />
        <path d="M40 60 C50 50 62 46 62 32 C52 32 42 40 40 52 Z" fill="#1F4A78" />
        <path d="M40 64 C32 56 26 50 28 38 Q34 42 40 56" stroke={ORANGE} strokeWidth="1.6" fill="none" />
        <path d="M22 70 L58 70 L54 92 L26 92 Z" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
        <rect x="22" y="68" width="36" height="6" rx="2" fill={NAVY} />
    </svg>
);

/* ---------- 7. Lamp ---------- */
export const LampSticker = (props) => (
    <svg viewBox="0 0 80 110" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="40" cy="102" rx="22" ry="3" fill={SHADOW} />
        <path d="M22 24 L58 24 L52 50 L28 50 Z" fill={ORANGE} stroke={NAVY} strokeWidth="2" />
        <rect x="38" y="50" width="4" height="42" fill={NAVY} />
        <ellipse cx="40" cy="96" rx="14" ry="4" fill={NAVY} />
        <ellipse cx="40" cy="22" rx="20" ry="4" fill={CREAM} stroke={NAVY} strokeWidth="2" />
        <circle cx="40" cy="62" r="1.6" fill="#FFF" />
        <path d="M22 16 q18 -10 36 0" stroke={ORANGE} strokeWidth="1.4" strokeDasharray="2 2" />
    </svg>
);

/* ---------- 8. Checkmark stamp ---------- */
export const CheckStampSticker = (props) => (
    <svg viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <circle cx="45" cy="45" r="38" fill={ORANGE} />
        <circle cx="45" cy="45" r="32" fill="none" stroke="#FFF" strokeWidth="2" strokeDasharray="3 4" />
        <path d="M28 46 L40 58 L62 34" stroke="#FFF" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ---------- 9. Furniture (chair) ---------- */
export const ChairSticker = (props) => (
    <svg viewBox="0 0 80 110" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <ellipse cx="40" cy="100" rx="24" ry="3" fill={SHADOW} />
        <rect x="18" y="14" width="44" height="48" rx="6" fill={NAVY} />
        <rect x="22" y="22" width="36" height="34" rx="4" fill={ORANGE} />
        <rect x="14" y="62" width="52" height="10" rx="3" fill={NAVY} />
        <rect x="20" y="72" width="6" height="26" fill={NAVY} />
        <rect x="54" y="72" width="6" height="26" fill={NAVY} />
    </svg>
);

/* ---------- 10. Sparkle / arrow / heart accents ---------- */
export const SparkAccent = (props) => (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M20 2 L24 16 L38 20 L24 24 L20 38 L16 24 L2 20 L16 16 Z" fill={ORANGE} />
    </svg>
);

export const HeartAccent = (props) => (
    <svg viewBox="0 0 40 36" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M20 34 C8 24 2 18 2 11 C2 5 7 1 12 1 C16 1 19 4 20 6 C21 4 24 1 28 1 C33 1 38 5 38 11 C38 18 32 24 20 34 Z" fill={ORANGE} />
    </svg>
);

export const ArrowSwoosh = (props) => (
    <svg viewBox="0 0 80 30" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M4 22 Q24 4 60 14" stroke={NAVY} strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray="2 5" />
        <path d="M54 6 L62 14 L54 22" stroke={NAVY} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
);

export const LeafAccent = (props) => (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M4 36 C4 14 22 4 38 4 C38 22 26 36 4 36 Z" fill="#1F4A78" />
        <path d="M6 34 Q22 20 36 6" stroke="#FFF" strokeWidth="1.4" fill="none" />
    </svg>
);

const STICKERS = {
    truck: TruckSticker,
    boxes: BoxStackSticker,
    mover: MoverPersonSticker,
    house: HousePinSticker,
    keys: KeyTagSticker,
    plant: PlantSticker,
    lamp: LampSticker,
    check: CheckStampSticker,
    chair: ChairSticker,
    spark: SparkAccent,
    heart: HeartAccent,
    arrow: ArrowSwoosh,
    leaf: LeafAccent,
};

/**
 * <CartoonSticker> — wraps any of the SVGs above with entrance + float animation.
 */
export const CartoonSticker = ({
    name,
    width = 96,
    height,
    rotate = 0,
    delay = 0,
    float = "y",
    className = "",
    style = {},
}) => {
    const Comp = STICKERS[name];
    if (!Comp) return null;
    const animClass = float === "big" ? "float-y-big" : float === "slow" ? "float-y-slow" : float === "wiggle" ? "wiggle" : "float-y";
    return (
        <motion.span
            initial={{ opacity: 0, y: 14, scale: 0.85, rotate: rotate - 10 }}
            whileInView={{ opacity: 1, y: 0, scale: 1, rotate }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
            className={`pointer-events-none absolute inline-flex ${animClass} ${className}`}
            style={{ width, height: height ?? width, ...style }}
        >
            <Comp width="100%" height="100%" />
        </motion.span>
    );
};

export default CartoonSticker;
